/**
 * `mount()` — public entry point for script/SDK embedding.
 *
 * POL-582 wires the real Shadow DOM root + theme-token pipeline in.
 * Mode renderers (single polst, campaign, feed) land in later tickets
 * and will plug into the shadow root returned by `createRoot`.
 */

import type { BrandThemeDTO } from "./brand-theme";
import { createRoot, type ShadowRootBundle } from "./shadow-root";
import { normalizeThemeMode, type ThemeMode } from "./theme";

export interface MountOptions {
  /** Which widget mode to render (polst, campaign, brand-feed, ...). */
  mode?: string;
  /** Configuration bag forwarded to the mode renderer. */
  config?: Record<string, unknown>;
  /** REST API base URL override. */
  baseUrl?: string;
  /** Theme mode: "light", "dark", or "auto" (default). */
  theme?: ThemeMode;
  /**
   * POL-644 — Resolved brand theme returned by the public REST DTO.
   * Forwarded to `createRoot()`; see its docs for the precedence chain.
   */
  brandTheme?: BrandThemeDTO | null;
}

export interface MountHandle {
  /** The open ShadowRoot the mode renderer writes into. */
  readonly shadowRoot: ShadowRoot;
  /** Tear the widget back down and detach the Shadow DOM contents. */
  unmount(): void;
}

export function mount(hostElement: Element, options: MountOptions = {}): MountHandle {
  if (!hostElement || typeof (hostElement as Element).appendChild !== "function") {
    throw new Error("mount(): hostElement must be a DOM Element");
  }

  const bundle: ShadowRootBundle = createRoot(hostElement, {
    theme: normalizeThemeMode(options.theme),
    brandTheme: options.brandTheme,
  });

  // Mode renderer wiring lands in follow-up tickets. For now we expose
  // the open ShadowRoot so downstream work (and integration tests) can
  // start attaching content immediately.
  return {
    shadowRoot: bundle.root,
    unmount(): void {
      bundle.dispose();
    },
  };
}
